import { useEffect, useCallback } from 'react';
import { AppIcon } from '../types/apps';
import { iconService } from '../services/iconService';
/**
 * Custom React hook for managing document title and favicon dynamically.
 * 
 * This hook provides a clean interface for updating the browser tab title
 * and favicon based on the current page or application state. It supports:
 * - Dynamic title composition with optional subtitle and base name
 * - Full title override when needed
 * - Emoji or URL-based favicon updates
 * - App definition-based favicon from AppIcon
 * - Automatic brand detection to avoid duplicate "River" in titles
 */

interface TitleConfig {
  title: string;
  subtitle?: string;
  baseName?: string;
  // When true, `title` is treated as the full document title with no base suffix appended
  useFullTitle?: boolean;
  // Optionally set a favicon via emoji or URL. If both provided, URL wins
  faviconEmoji?: string;
  faviconUrl?: string;
  // Use app definition icon for favicon
  appId?: string;
}

const DEFAULT_BASE_NAME = 'River';

// Environment detection for favicon
const isProduction = () => {
  const hostname = window.location.hostname;
  return hostname === 'rivereditor.com' || hostname.endsWith('.rivereditor.com');
};

const setEnvironmentFavicon = () => {
  const faviconPath = isProduction() ? '/favicon.ico' : '/favicon-dev.svg';
  setUrlFavicon(faviconPath);
};

// Export function to set environment favicon on app initialization
export const initializeEnvironmentFavicon = () => {
  setEnvironmentFavicon();
};

export { setUrlFavicon, setEnvironmentFavicon };

const setEmojiFavicon = (emoji: string) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64"><text x="50%" y="50%" dominant-baseline="central" text-anchor="middle" font-size="52">${emoji}</text></svg>`;
  const url = `data:image/svg+xml,${encodeURIComponent(svg)}`;
  let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
};

const setUrlFavicon = (href: string) => {
  let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = href;
};

const setAppIconFavicon = (appIcon: AppIcon) => {
  if (appIcon.type === 'emoji' && appIcon.emoji) {
    setEmojiFavicon(appIcon.emoji);
  } else if (appIcon.type === 'image' && (appIcon.imagePath || appIcon.src)) {
    setUrlFavicon(appIcon.imagePath || appIcon.src || '');
  }
  // For phosphor, gradient, component types - no conversion, skip favicon update
  // These would require complex SVG generation that's not worth implementing
};

export const useDocumentTitle = ({ title, subtitle, baseName = DEFAULT_BASE_NAME, useFullTitle, faviconEmoji, faviconUrl, appId }: TitleConfig) => {
  const updateTitle = useCallback(() => {
    const titleIncludesBrand = typeof title === 'string' && title.toLowerCase().includes('river');
    const shouldUseFullTitle = Boolean(useFullTitle || titleIncludesBrand);

    let fullTitle = '';
    if (shouldUseFullTitle) {
      fullTitle = title || baseName;
    } else if (subtitle) {
      fullTitle = `${title} - ${subtitle} - ${baseName}`;
    } else if (title) {
      fullTitle = `${title} - ${baseName}`;
    } else {
      fullTitle = baseName;
    }

    document.title = fullTitle;

    // Favicon: URL takes precedence, else emoji, else app icon, else special-case by title
    if (faviconUrl) {
      setUrlFavicon(faviconUrl);
    } else if (faviconEmoji) {
      setEmojiFavicon(faviconEmoji);
    } else if (appId) {
      // Use app definition icon for favicon
      try {
        const appIcon = iconService.getAppIcon(appId);
        setAppIconFavicon(appIcon);
      } catch (error) {
        console.warn(`Failed to get app icon for ${appId}:`, error);
        setEnvironmentFavicon();
      }
    } else {
      setEnvironmentFavicon();
    }
  }, [title, subtitle, baseName, useFullTitle, faviconEmoji, faviconUrl, appId]);

  useEffect(() => {
    updateTitle();
  }, [updateTitle]);

  return { updateTitle };
};

// Specific hooks for common patterns
export const useSpaceTitle = (spaceName?: string) => {
  return useDocumentTitle({
    title: spaceName || 'Loading...',
    subtitle: spaceName ? undefined : undefined,
  });
};

export const usePageTitle = (pageName: string, detail?: string, appId?: string) => {
  return useDocumentTitle({
    title: pageName,
    subtitle: detail,
    appId,
  });
}; 